import classNames from 'styles/utils/classNames';

export interface FilterChipOption<T> {
  label: string;
  value: T;
  /** Colored icon shown inside the chip (greyed when unselected) — sport chips. */
  iconSrc?: string;
}

interface Props<T extends string | number> {
  options: FilterChipOption<T>[];
  /** Currently-active values. Single-select callers pass `[]` or `[one]`. */
  selected: T[];
  /** Toggle one value. Single-select callers replace; multi callers add/remove. */
  onToggle: (value: T) => void;
  /** "Filter elements" select to a dark pill; "Filter tab new" (Sport) to a white,
   *  bordered pill with an icon. */
  tone?: 'solid' | 'outline';
  /**
   * `fill` (default) spreads chips to equal widths across a wrapping row — the
   * filters sheet. `hug` sizes each chip to its label and scrolls sideways instead
   * of wrapping — the chat list's `All / Unread 3 / Requests 1` tab strip, where
   * labels carry counts and so vary in width.
   */
  layout?: 'fill' | 'hug' | 'grid';
  /** Chip height: `sm` = 32px (the /find sheet), `md` = 40px (home sheet "Filter elements"). */
  size?: 'sm' | 'md';
  /** `grid` only: equal columns, so every row lines up (Figma's 3-up filter grid). */
  columns?: 2 | 3 | 4;
  /**
   * Label size. Defaults to `sm`, which is what every existing caller renders.
   * Figma's "Filter tab new" specifies Prod/Button label M, so the chat tab strip
   * passes `md`; the filters sheet is left as-is rather than restyled in passing.
   */
  labelSize?: 'sm' | 'md';
  /** Renders a leading chip (e.g. "All" / "Any") that clears the selection; it reads
   *  as active while nothing else is selected. */
  resetLabel?: string;
  onReset?: () => void;
}

const CHIP_BASE =
  'flex items-center justify-center gap-ds-xs rounded-md px-ds-md transition-colors';
const CHIP_HEIGHT = { sm: 'h-8', md: 'h-10' } as const;

const CHIP_LABEL_SM = 'typography-product-button-label-small';
const CHIP_LABEL_MD = 'typography-product-button-label-medium';

const CHIP_FILL = 'min-w-[6rem] flex-1';
const CHIP_HUG = 'shrink-0';

const ROW_FILL = 'flex flex-wrap gap-ds-lg';
// Setting `overflow-x` to a non-visible value makes `overflow-y` compute to auto too,
// so the row clips the selected chip's shadow on every side. The negative margin plus
// matching padding bleeds the scroll box outwards to make room while keeping the chips
// aligned with the content above. The selected shadow is `0 4px 10px`, so it needs ~14px
// — 4px was not enough.
const ROW_HUG = 'no-scrollbar -m-4 flex gap-ds-sm overflow-x-auto p-4';

const GRID_COLS: Record<2 | 3 | 4, string> = {
  2: 'grid-cols-2',
  3: 'grid-cols-3',
  4: 'grid-cols-4',
};

const CHIP_UNSELECTED =
  'bg-color-bg-lightmode-secondary text-color-text-lightmode-secondary dark:bg-color-bg-darkmode-secondary dark:text-color-text-darkmode-secondary';

const CHIP_SELECTED_SOLID =
  'bg-color-bg-lightmode-invert text-color-text-lightmode-invert dark:bg-color-bg-darkmode-invert dark:text-color-text-darkmode-invert';

const CHIP_SELECTED_OUTLINE =
  'bg-color-bg-lightmode-primary text-color-text-lightmode-primary shadow-[0px_4px_10px_0px_rgba(0,0,0,0.16)] ring-1 ring-color-border-selected-lightmode dark:bg-color-bg-darkmode-primary dark:ring-color-border-selected-darkmode dark:text-color-text-darkmode-primary';

/**
 * A row of selectable filter chips (Distance, Sport, Time, Type, …). Chips fill the
 * row equally and wrap — matching the Figma "Filter elements" / "Filter tab new"
 * components. The parent owns selection semantics: single-select sections replace on
 * toggle, multi-select add/remove, and an optional `resetLabel` chip clears.
 */
export default function FilterChipGroup<T extends string | number>({
  options,
  selected,
  onToggle,
  tone = 'solid',
  layout = 'fill',
  columns = 3,
  size = 'sm',
  labelSize = 'sm',
  resetLabel,
  onReset,
}: Props<T>) {
  const selectedClass = tone === 'outline' ? CHIP_SELECTED_OUTLINE : CHIP_SELECTED_SOLID;
  const sizeClass = classNames(
    CHIP_HEIGHT[size],
    layout === 'hug' ? CHIP_HUG : layout === 'grid' ? 'min-w-0' : CHIP_FILL,
    labelSize === 'md' ? CHIP_LABEL_MD : CHIP_LABEL_SM,
  );
  const nothingSelected = selected.length === 0;

  return (
    <div
      className={
        layout === 'hug'
          ? ROW_HUG
          : layout === 'grid'
          ? classNames('grid gap-ds-lg', GRID_COLS[columns])
          : ROW_FILL
      }
    >
      {resetLabel && (
        <button
          type="button"
          onClick={onReset}
          className={classNames(
            CHIP_BASE,
            sizeClass,
            nothingSelected ? selectedClass : CHIP_UNSELECTED,
          )}
        >
          <span className="whitespace-nowrap">{resetLabel}</span>
        </button>
      )}

      {options.map((option) => {
        const isSelected = selected.includes(option.value);

        return (
          <button
            key={String(option.value)}
            type="button"
            onClick={() => onToggle(option.value)}
            className={classNames(
              CHIP_BASE,
              sizeClass,
              isSelected ? selectedClass : CHIP_UNSELECTED,
            )}
          >
            {option.iconSrc && (
              <img
                src={option.iconSrc}
                alt=""
                className={classNames(
                  'h-5 w-5 shrink-0 object-contain',
                  !isSelected && 'opacity-60 grayscale',
                )}
              />
            )}
            <span className="whitespace-nowrap">{option.label}</span>
          </button>
        );
      })}
    </div>
  );
}
